import React, { useEffect, useState } from "react";
import { getSettings, saveSettings } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { X, Settings2, Loader2 } from "lucide-react";

// Field-mapping settings: control the custom-variable names & which extras
// get included in CSV export and Instantly push.
export default function SettingsModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) getSettings().then(setS).catch(() => {});
  }, [open]);

  if (!open) return null;

  const upd = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveSettings(s);
      toast({ title: "Mapping saved" });
      onSaved && onSaved(saved);
      onClose();
    } catch (e) {
      toast({ title: "Could not save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    { label: "Address", inc: "include_address", var: "address_var" },
    { label: "Rating", inc: "include_rating", var: "rating_var" },
    { label: "Reviews", inc: "include_reviews", var: "reviews_var" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-border w-full max-w-lg p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-[18px] font-semibold text-foreground flex items-center gap-2"><Settings2 size={17} className="text-brand-600" /> Instantly field mapping</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <p className="text-[13px] text-muted-foreground mb-5">
          Standard fields (email, company_name, phone, website) are sent automatically. Set the custom-variable names below to match the <code className="text-foreground">{"{{tags}}"}</code> in your Instantly campaign.
        </p>

        {!s ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 size={20} className="mx-auto animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-[110px_1fr_auto] gap-3 items-center text-[12px] font-medium text-muted-foreground px-1">
              <span>Field</span><span>Variable name</span><span>Include</span>
            </div>
            {rows.map((r) => (
              <div key={r.var} className="grid grid-cols-[110px_1fr_auto] gap-3 items-center">
                <span className="text-[14px] text-foreground">{r.label}</span>
                <input value={s[r.var]} onChange={(e) => upd(r.var, e.target.value)}
                  disabled={!s[r.inc]}
                  className="border border-border rounded-lg px-3 py-2 text-[14px] outline-none focus:border-brand-500 disabled:bg-secondary disabled:text-muted-foreground" />
                <button onClick={() => upd(r.inc, !s[r.inc])}
                  className={`w-9 h-5 rounded-full transition-colors relative ${s[r.inc] ? "bg-brand-600" : "bg-gray-300"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${s[r.inc] ? "left-[18px]" : "left-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[14px] text-muted-foreground hover:bg-secondary">Cancel</button>
          <button onClick={save} disabled={saving || !s} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 text-white text-[14px] font-medium disabled:opacity-50 hover:bg-brand-700">
            {saving && <Loader2 size={15} className="animate-spin" />} Save mapping
          </button>
        </div>
      </div>
    </div>
  );
}
